/**
 * Web app portal - equipment and after-service (SD-S3-12): `/chat/<slug>/equipment`, `/contracts`, `/projects` (with the acceptance),
 * `/visits` and `/returns`. Words follow `mockups/tickets/widget-equipment.html` (E1-E5) and `client-acceptance.html` (A1-A4); what the
 * server itself says (a return's status label, shipping instructions, why a visit cannot be moved) is shown as sent. A warranty badge
 * states a status and a date - never a coverage the workspace did not link terms for.
 */
export const webappPortalAfterSales = {
  nav: { area: "Equipment and service", equipment: "Equipment", service: "Service", contracts: "Contracts", projects: "Installations", visits: "Visits", returns: "Returns" },
  off: { title: "Equipment and service are not available here", body: "This workspace does not show equipment, visits or installations in the portal. You can still raise a request and we will help.", cta: "Go to my tickets" },
  common: {
    retry: "Try again",
    loadMore: "Show more",
    none: "Not recorded",
    offline: "You seem to be offline. Check your connection and try again.",
    limited: "You have tried this a few times. Wait a moment, then try again.",
    notFound: "We could not find that. It may belong to another account.",
    forbidden: "You do not have access to this.",
    failed: "Something went wrong on our side. Please try again.",
    downloaded: "{{name}} was downloaded",
    downloadFailed: "We could not download the file. Please try again.",
  },
  warranty: {
    until: "In warranty until {{date}}",
    endsIn_one: "Warranty ends in {{count}} day",
    endsIn_other: "Warranty ends in {{count}} days",
    expired: "Warranty expired {{date}}",
    unknown: "Warranty not recorded",
    ends: "Warranty ends {{date}}",
    covers: "The linked warranty terms cover: {{cover}}.",
    teamConfirms: "The team will confirm what the warranty covers.",
    cover: { parts: "parts", labor: "labour", travel: "travel", onsite: "on-site service" },
    filter: { in_warranty: "In warranty", expiring: "Expiring soon", expired: "Expired", unknown: "Not recorded" },
  },
  equipment: {
    title: "My equipment",
    count_one: "{{count}} unit",
    count_other: "{{count}} units",
    search: "Search equipment",
    searchHint: "Name, tag or serial",
    filter: "Warranty",
    allWarranty: "All equipment",
    categoryFilter: "Category",
    allCategories: "All categories",
    clear: "Clear the search",
    emptyTitle: "No equipment yet",
    emptyBody: "The equipment your organisation owns appears here once the team adds it. You can still report a problem and we will help.",
    emptyFiltered: "No equipment matches",
    emptyFilteredHint: "Try another name, tag or serial number, or clear the filter.",
    errorTitle: "We could not load your equipment",
    open_: "Open {{name}}, {{tag}}",
    back: "Back to my equipment",
    detailErrorTitle: "We could not load this equipment",
    notFoundTitle: "We could not find this equipment",
    notFoundBody: "It may have been moved, or it belongs to another account. Go back to your list to see your equipment.",
    labelTitle: "This label is not for your account",
    detailsTitle: "Details",
    serial: "Serial number",
    model: "Model",
    category: "Category",
    location: "Location",
    installed: "Installed",
    warrantyTitle: "Warranty",
    contractTitle: "Service contract",
    noContract: "No service contract covers this equipment.",
    documentsTitle: "Documents",
    noDocuments: "No documents have been shared for this equipment yet.",
    download: "Open",
    openDoc: "Open {{title}}",
    visitsTitle: "Visits",
    noVisits: "No visits for this equipment yet.",
    report: "Report a problem with this equipment",
    requestReturn: "Request a return",
  },
  contracts: {
    title: "My contracts",
    intro: "What your service contracts include and how much you have used. Prices and internal terms are not shown here.",
    emptyTitle: "No service contracts yet",
    emptyBody: "When a service contract is set up for your organisation, its dates and what it includes appear here.",
    errorTitle: "We could not load your contracts",
    valid: "Valid {{from}} to {{to}}",
    daysLeft_one: "{{count}} day left",
    daysLeft_other: "{{count}} days left",
    covers: "Covers",
    cover: { labor: "labour", parts: "parts", travel: "travel" },
    coverNone: "see the team for what is included",
    exclusions: "Not included: {{text}}",
    allAssets: "All your equipment is covered. See my equipment",
    someAssets_one: "Covers {{count}} unit",
    someAssets_other: "Covers {{count}} units",
    entitlements: "Included in the contract",
    remaining: "{{left}} of {{total}} left",
    kind: { pm_visits: "Preventive maintenance visits", calibrations: "Calibrations", onsite_visits: "On-site visits", remote_support_hours: "Remote support hours", ticket_allowance: "Requests" },
    period: { contract: "For the whole contract", year: "Resets {{date}}", month: "Resets {{date}}" },
  },
  projects: {
    // The neutral words, used until the workspace vocabulary is known and where it names no plural.
    words: { project: "Installation", projects: "Installation projects", stage: "Stage", stages: "Stages", area: "Installations" },
    title: "My {{areaLc}}",
    intro: "Your {{projectsLc}}: where each one stands, the open items you can confirm, and the acceptance.",
    emptyTitle: "No {{areaLc}} yet",
    emptyBody: "{{projects}} for your organisation appear here while they are under way.",
    errorTitle: "We could not load your {{areaLc}}",
    detailErrorTitle: "We could not load this {{projectLc}}",
    notFoundTitle: "We could not find this {{projectLc}}",
    back: "Back to my {{areaLc}}",
    open_: "Open {{projectLc}} {{number}}, {{name}}",
    stages: "{{done}} of {{total}} {{stagesLc}} done",
    openItems_one: "{{count}} open item",
    openItems_other: "{{count}} open items",
    acceptedOn: "Accepted {{date}}",
    plannedEnd: "Planned finish {{date}}",
    assets: "Equipment",
    progress: "Progress",
    documents: "Documents",
    noDocuments: "No documents have been shared for this {{projectLc}} yet.",
    checklist: "{{done}}/{{total}} checks",
    status: { planned: "Planned", in_progress: "In progress", punch_list: "Open items", ready_for_acceptance: "Ready for acceptance", handed_over: "Handed over", on_hold: "On hold", cancelled: "Cancelled" },
    stageStatus: { pending: "Not started", in_progress: "In progress", passed: "Done", failed: "Needs another try" },
  },
  punch: {
    status: { open: "Open", in_progress: "Being fixed", fixed: "Fixed - please confirm", verified: "Confirmed", reopened: "Reopened", waived: "Closed" },
    empty: "Nothing is open. Raise an issue if you notice something that is not right.",
    raise: "Raise an issue",
    raiseTitle: "Raise an issue",
    raiseHint: "We record it on the {{projectLc}} and open a request so you can talk to the team about it.",
    raiseSubmit: "Send",
    cancel: "Cancel",
    fieldTitle: "What is wrong?",
    fieldDescription: "More detail (optional)",
    fieldLocation: "Where (optional)",
    fieldAsset: "Equipment (optional)",
    noAsset: "Not about one unit",
    titleRequired: "Tell us in a few words what is wrong.",
    raisedToast: "Issue raised. Follow it in request {{number}}.",
    raisedPlain: "Issue raised",
    confirmFixed: "Confirm fixed",
    notFixed: "Not fixed",
    verifyGroup: "Is {{number}} fixed?",
    confirmedToast: "{{number}} confirmed as fixed",
    reopenedToast: "{{number}} sent back to the team",
    fixNote: "The team says: {{note}}",
    ticket: "Request {{number}}",
  },
  accept: {
    title: "Acceptance",
    ready: "Everything is verified. The {{projectLc}} can be accepted now.",
    blocked: "This {{projectLc}} cannot be accepted yet:",
    blockedStage: "The {{stageLc}} \"{{stage}}\" has not passed.",
    blockedItems_one: "{{count}} open item still to be confirmed.",
    blockedItems_other: "{{count}} open items still to be confirmed.",
    adminOnly: "Only an organisation admin can sign the acceptance. Ask an administrator of your organisation to accept this {{projectLc}}.",
    accepted: "Accepted on {{date}} by {{name}}. The warranty on the equipment is running.",
    comment: "Comment: {{text}}",
    start: "Review and accept",
    certificate: "Download the certificate",
    preview: "Preview the certificate",
    step: "Step {{n}} of {{total}}",
    reviewTitle: "Review the {{projectLc}}",
    reviewBody: "{{assets}} unit(s), {{stages}} {{stageLc}}(s) done and {{items}} item(s) in the list, all verified or closed. Look through the equipment, the {{stagesLc}}, the items and the documents above before you continue.",
    reviewed: "I have reviewed the equipment, {{stagesLc}}, items and documents above.",
    continue: "Continue",
    back: "Back",
    codeTitle: "Enter the code we sent",
    codeSent: "Signing needs a one-time code. We sent a 6-digit code to {{email}}.",
    codeLabel: "6-digit code",
    codeBad: "Enter the 6 digits of the code.",
    resend: "Send a new code",
    signTitle: "Sign",
    method: "How you sign",
    draw: "Draw",
    type: "Type your name",
    padLabel: "Signature pad. Use \"Type your name\" if you cannot draw.",
    clear: "Clear",
    typedLabel: "Type your full name",
    typedHint: "A typed name is as valid as a drawn signature.",
    titleLabel: "Your title (optional)",
    commentsLabel: "Comment (optional)",
    confirm: "I accept the {{projectLc}} {{name}} on behalf of my organisation. The warranty on each unit starts on the date I sign.",
    submit: "Sign and accept",
    problem: {
      wrong_code: "That code is not correct. Check it and try again.",
      code_expired: "That code has expired. We sent a new one.",
      locked: "Too many attempts. We sent a new code.",
      admin_only: "Only an organisation admin can accept the {{projectLc}}.",
      not_ready: "This {{projectLc}} is not ready for acceptance any more. Reload the page to see why.",
      signature: "We could not read the signature. Please sign again.",
      limited: "You have tried this a few times. Wait a moment, then try again.",
      offline: "You seem to be offline. Check your connection and try again.",
      failed: "We could not record the acceptance. Please try again.",
    },
  },
  visits: {
    title: "My visits",
    intro: "Visits our engineers make to your site, past and coming up.",
    upcoming: "Upcoming ({{count}})",
    past: "Past ({{count}})",
    emptyUpcoming: "No visits coming up",
    emptyPast: "No past visits yet",
    emptyBody: "When the team schedules a visit for one of your requests, it appears here with the time and the engineer.",
    errorTitle: "We could not load your visits",
    detailErrorTitle: "We could not load this visit",
    notFoundTitle: "We could not find this visit",
    back: "Back to my visits",
    open_: "Open visit {{number}}",
    chooseBadge: "Choose a time",
    waitingChoice: "Waiting for the time you choose",
    status: { proposed: "Choose a time", scheduled: "Scheduled", dispatched: "Engineer on the way", on_site: "Engineer on site", completed: "Completed", cannot_complete: "Could not be completed", cancelled: "Cancelled", no_access: "No access" },
    kind: { corrective: "Repair visit", pm: "Preventive maintenance", calibration: "Calibration", installation: "Installation", inspection: "Inspection", training: "Training" },
    chooseTitle: "Choose a time for the visit",
    chooseBody: "Pick the time that suits you. We confirm it straight away.",
    windows: "Visit times",
    confirmWindow: "Confirm this time",
    taken: "That window was just taken. Please choose another time below.",
    takenNone: "That window was just taken and no other time is on offer right now. The team will get in touch to agree one.",
    chosenToast: "Visit confirmed",
    scheduleTitle: "Visit",
    engineer: "Engineer",
    where: "Where",
    kindLabel: "Type",
    equipment: "Equipment",
    prep: "Before we arrive",
    calendar: "Add to calendar (.ics)",
    reschedule: "Ask to reschedule",
    rescheduleTitle: "Ask for another time",
    rescheduleLabel: "When would suit you?",
    rescheduleHint: "Tell us the days or times that work. The team confirms a new time.",
    rescheduleSubmit: "Send request",
    rescheduleSent: "Request sent. The team will confirm a new time.",
    rescheduleRequested: "You asked for another time. The team will confirm it.",
    noteShort: "Tell us a little more (at least {{min}} characters).",
    cancel: "Cancel the visit",
    cancelTitle: "Cancel this visit",
    cancelLabel: "Reason (optional)",
    cancelHint: "You can cancel up to 24 hours before the visit.",
    cancelSubmit: "Cancel the visit",
    cancelled: "The visit was cancelled",
    keep: "Keep the visit",
    reportTitle: "Visit report",
    resolved: "The issue was resolved on this visit.",
    notResolved: "The issue was not fully resolved on this visit.",
    followUp: "A follow-up visit is needed. The team will be in touch.",
    work: "Work done",
    findings: "Findings",
    parts: "Parts used",
    time: "Time on site",
    minutes_one: "{{count}} minute",
    minutes_other: "{{count}} minutes",
    signedBy: "Signed by",
    reportPdf: "Visit report (PDF)",
  },
  returns: {
    title: "My returns",
    intro: "Items you sent back or are sending back, and where each one stands.",
    emptyTitle: "No returns yet",
    emptyBody: "When you return an item for repair or replacement, you can follow it here.",
    toEquipment: "Go to my equipment",
    errorTitle: "We could not load your returns",
    detailErrorTitle: "We could not load this return",
    notFoundTitle: "We could not find this return",
    back: "Back to my returns",
    open_: "Open return {{number}}",
    withManufacturer: "Your item is with the manufacturer. We will update this page when it comes back.",
    detailsTitle: "Return",
    item: "Item",
    request: "Request",
    opened: "Opened",
    instructions: "How to send it",
    tracking: "Tracking",
    noTracking: "No tracking reference yet.",
    trackingLabel: "Tracking reference of your parcel",
    trackingHint: "The number your courier gave you, so we can follow the parcel.",
    trackingSave: "Save the reference",
    trackingRequired: "Enter the tracking reference.",
    trackingSaved: "Tracking reference saved",
  },
  field: {
    label: "Which of your equipment is this about?",
    notListed: "Not listed",
    more: "Showing {{shown}} of {{total}}. Search to find another.",
    failed: "We could not load your equipment. You can describe it in the details instead.",
    about: "This request is about {{name}} ({{tag}}).",
  },
};
